import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { addressToString, deserializeCV } from '@stacks/transactions';
import useNetwork from 'hooks/use-network';
import { LinkIcon16 } from 'modules/icons/LinkIcon16';
import { getTxnExplorerUrl } from 'utils/helper';

import { DISPLAY_DIGITS_AFTER_ZERO } from '../../../constants/base';
import useGetTokenMetadata from '../../../hooks/use-getTokenMetadata';
import { DisplayMetadataInActivityType } from '../../../types';
import { displayFullAmount, formatBalance } from '../../../utils/balance-tools';
import { WalletWithIcons } from '../../Wallet/WalletWithIcons/WalletWithIcons';

import { useTransactionNonStxTokenStyles } from './useTransactionNonStxTokenStyles';

type TransactionNonStxTokenTypes = {
  payload: any;
  txId: string;
  notes?: JSX.Element;
};

export const TransactionNonStxToken = ({ payload, txId, notes }: TransactionNonStxTokenTypes) => {
  const [tokenData, setTokenData] = useState<DisplayMetadataInActivityType>();
  const classes = useTransactionNonStxTokenStyles();
  const [showRealBalance, setShowRealBalance] = useState(false);
  const [network] = useNetwork();

  const { fetchTokenMetadata } = useGetTokenMetadata();
  useEffect(() => {
    if (tokenData) return;
    const amount = deserializeCV(payload.functionArgs[0]);
    let receiverAddress = '';
    try {
      // @ts-ignore
      receiverAddress = addressToString(deserializeCV(payload.functionArgs[2])?.address);
    } catch (e) {
      console.error(JSON.stringify(e));
    }

    fetchTokenMetadata(payload.contractAddress, payload.contractName).then((tokenAsset) => {
      if ('value' in amount && tokenAsset) {
        if (!tokenAsset.decimals) return;
        const amountToDisplay = formatBalance(
          Number(amount.value) / Math.pow(10, tokenAsset.decimals),
          DISPLAY_DIGITS_AFTER_ZERO
        );
        setTokenData({
          ...tokenAsset,
          amount: Number(
            (Number(amount.value) / Math.pow(10, tokenAsset.decimals)).toFixed(tokenAsset.decimals)
          ),
          amountToDisplay: +amountToDisplay,
          to: receiverAddress,
        });
      }
    });
  }, [payload.contractAddress, payload.contractName, tokenData]);

  return (
    <div className="flex gap-4 flex-wrap">
      {tokenData ? (
        <div
          className={classes.title}
          onMouseEnter={() => setShowRealBalance(true)}
          onMouseLeave={() => setShowRealBalance(false)}
        >
          {showRealBalance && tokenData && tokenData.amountToDisplay === 0 && (
            <div className={classes.amountTooltip}>
              {`${displayFullAmount(tokenData?.amount, tokenData?.decimals)} ${tokenData?.symbol}`}
            </div>
          )}
          {tokenData.amountToDisplay === 0
            ? `Transfer <0.000001 
        ${tokenData?.symbol || payload.contractName}`
            : `Transfer ${tokenData.amountToDisplay}
        ${tokenData?.symbol || payload.contractName}`}
        </div>
      ) : (
        <div className={classes.title}>{`Transfer ${payload.contractName}`}</div>
      )}
      {notes}
      {tokenData?.to && (
        <Box display="flex" gap="8px" alignItems="center" flexDirection="row">
          <span className={classes.wallet}>To: </span>
          <WalletWithIcons address={tokenData?.to} />
        </Box>
      )}
      {Boolean(txId) && (
        <a
          href={getTxnExplorerUrl(txId, network)}
          target="_blank"
          rel="noreferrer"
          className={classes.linkIcon}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <LinkIcon16 />
        </a>
      )}
    </div>
  );
};
